import { PLANNING_AKB_MAX, PLANNING_AMOUNT_MAX } from '@crm/shared'

/**
 * Ввод сумм и количеств в «Планировании» (17.09.2026).
 *
 * Раньше поле выкидывало всё, кроме цифр: «1 140 415,32» из выписки
 * бухгалтерии сохранялось как 114 041 532, «1500.50» — как 150 050, а «+50 000»
 * перезаписывало факт вместо прибавки. Теперь:
 *  - запятая и точка — разделитель копеек; если в строке есть оба знака,
 *    копейки отделяет последний, остальные — разряды («1,140,415.32»);
 *  - «+» в начале прибавляет к текущему значению, «−» вычитает (не ниже нуля);
 *  - больше двух знаков после запятой — ошибка, а не молчаливое округление:
 *    «1,500» в английской записи — это тысяча пятьсот, а не полтора рубля.
 *
 * Считаем в целых единицах (копейки / штуки), чтобы не ловить float-хвосты.
 */

/** money — рубли с копейками, count — штуки (АКБ). */
export type AmountKind = 'money' | 'count'

export type AmountDraftResult =
  | { ok: true; value: number; relative: boolean }
  | { ok: false; error: string }

const NBSP = '\xA0'
const MINUS = '\u{2212}'
// Знак в начале: «+» — прибавить; дефис, минус (U+2212), короткое и длинное тире — вычесть.
const SIGN_RE = /^\s*([+\-\u{2212}\u{2013}\u{2014}])\s*/u
// Валюта вокруг числа из выписки: «₽», «руб.», «рублей», «RUB», «р.» в конце.
const CURRENCY_RE = /₽|руб[а-яё]*\.?|(?:rub|rur)\.?/giu
const TRAILING_R_RE = /\s*р\.?\s*$/iu
const SPACES_RE = /[\s'’]/g

const MAX_UNITS: Record<AmountKind, number> = {
  money: Math.round(PLANNING_AMOUNT_MAX * 100),
  count: PLANNING_AKB_MAX,
}

const ERR_NOT_NUMBER = 'Не похоже на число — нужны цифры, пробелы и запятая'
const ERR_GROUPS = 'Разряды разделены неверно — проверьте число'
const ERR_DECIMALS: Record<AmountKind, string> = {
  money: 'После запятой — не больше двух цифр (копейки)',
  count: 'Количество клиентов — целое число',
}

function tooBigError(kind: AmountKind): string {
  return kind === 'money'
    ? `Слишком большая сумма — максимум ${formatPlanningAmount(PLANNING_AMOUNT_MAX, 'money')} ₽`
    : `Слишком большое число — максимум ${formatPlanningAmount(PLANNING_AKB_MAX, 'count')}`
}

/** Значение → целые единицы: копейки для денег, штуки для АКБ. */
export function toUnits(value: number, kind: AmountKind): number {
  return kind === 'money' ? Math.round(value * 100) : Math.round(value)
}

function fromUnits(units: number, kind: AmountKind): number {
  return kind === 'money' ? units / 100 : units
}

/** «1140415» → «1 140 415» (неразрывные пробелы, как у toLocaleString). */
function groupDigits(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP)
}

function countChar(s: string, ch: string): number {
  return s.split(ch).length - 1
}

type SplitNumber = { ok: true; intDigits: string; hasSep: boolean; frac: string }

/** Разбор числа без знака на целую часть и копейки — без проверки лимитов. */
function splitNumber(text: string): SplitNumber | { ok: false; error: string } {
  const s = text.replace(CURRENCY_RE, '').replace(TRAILING_R_RE, '').replace(SPACES_RE, '')
  if (!/^[\d.,]*$/.test(s) || !/\d/.test(s)) return { ok: false, error: ERR_NOT_NUMBER }

  const commas = countChar(s, ',')
  const dots = countChar(s, '.')
  let decimalSep: ',' | '.' | null = null
  if (commas > 0 && dots > 0) decimalSep = s.lastIndexOf(',') > s.lastIndexOf('.') ? ',' : '.'
  else if (commas === 1) decimalSep = ','
  else if (dots === 1) decimalSep = '.'

  let intPart = s
  let frac = ''
  if (decimalSep) {
    const at = s.lastIndexOf(decimalSep)
    intPart = s.slice(0, at)
    frac = s.slice(at + 1)
    if (intPart.includes(decimalSep)) return { ok: false, error: ERR_GROUPS }
  }

  // Что осталось из «,» и «.» в целой части — разделители разрядов по 3 цифры.
  const groupSep = [',', '.'].find((c) => intPart.includes(c))
  if (groupSep) {
    const [head, ...rest] = intPart.split(groupSep)
    if (!head || head.length > 3 || rest.some((g) => g.length !== 3)) {
      return { ok: false, error: ERR_GROUPS }
    }
    intPart = [head, ...rest].join('')
  }
  if (!/^\d*$/.test(intPart)) return { ok: false, error: ERR_GROUPS }

  return {
    ok: true,
    intDigits: intPart.replace(/^0+(?=\d)/, '') || '0',
    hasSep: decimalSep !== null,
    frac,
  }
}

function parseUnsigned(
  text: string,
  kind: AmountKind,
): { ok: true; units: number } | { ok: false; error: string } {
  const parts = splitNumber(text)
  if (!parts.ok) return parts
  if (parts.frac.length > (kind === 'money' ? 2 : 0))
    return { ok: false, error: ERR_DECIMALS[kind] }
  // 13 цифр × 100 ещё точно помещаются в double; больше — заведомо за лимитом.
  if (parts.intDigits.length > 13) return { ok: false, error: tooBigError(kind) }
  const whole = Number(parts.intDigits)
  const units = kind === 'money' ? whole * 100 + Number(parts.frac.padEnd(2, '0')) : whole
  return { ok: true, units }
}

/**
 * Что сохранить из поля: итог с учётом «+»/«−» относительно `current`.
 * Пустое поле — 0 (как и раньше), одинокий знак — без изменений.
 */
export function evaluateAmountDraft(
  draft: string,
  current: number,
  kind: AmountKind,
): AmountDraftResult {
  const sign = SIGN_RE.exec(draft)
  const rest = sign ? draft.slice(sign[0].length) : draft
  const currentUnits = toUnits(current, kind)
  if (rest.trim() === '') {
    return { ok: true, value: sign ? fromUnits(currentUnits, kind) : 0, relative: Boolean(sign) }
  }

  const parsed = parseUnsigned(rest, kind)
  if (!parsed.ok) return parsed

  let units = parsed.units
  if (sign?.[1] === '+') units = currentUnits + units
  else if (sign) units = Math.max(0, currentUnits - units)
  if (units > MAX_UNITS[kind]) return { ok: false, error: tooBigError(kind) }
  return { ok: true, value: fromUnits(units, kind), relative: Boolean(sign) }
}

/**
 * Показ значения: «1 140 415,32», целые рубли — без «,00» («16 700 000»),
 * количество — «1 000».
 */
export function formatPlanningAmount(value: number, kind: AmountKind): string {
  const units = toUnits(value, kind)
  if (kind === 'count') return groupDigits(String(units))
  const rubles = groupDigits(String(Math.floor(units / 100)))
  const kopecks = units % 100
  return kopecks === 0 ? rubles : `${rubles},${String(kopecks).padStart(2, '0')}`
}

/**
 * Формат поля во время набора: разряды и запятая вместо точки, знак — как
 * «+» / «−». Непонятный ввод не трогаем — ошибку покажем при выходе из поля.
 */
export function formatDraftLive(raw: string, kind: AmountKind): string {
  const sign = SIGN_RE.exec(raw)
  const signChar = sign ? (sign[1] === '+' ? '+' : MINUS) : ''
  const rest = sign ? raw.slice(sign[0].length) : raw
  if (rest.trim() === '') return signChar || raw
  const parts = splitNumber(rest)
  if (!parts.ok || (kind === 'count' && parts.hasSep)) return raw
  return signChar + groupDigits(parts.intDigits) + (parts.hasSep ? `,${parts.frac}` : '')
}

/**
 * Куда поставить курсор после переформатирования: за ту же по счёту цифру.
 * Курсор в конце строки остаётся в конце — самый частый случай набора.
 */
export function mapCaret(raw: string, caret: number, formatted: string): number {
  if (caret >= raw.length) return formatted.length
  const digitsBefore = raw.slice(0, caret).replace(/\D/g, '').length
  if (digitsBefore === 0) return Math.min(caret, formatted.search(/\d|$/))
  let seen = 0
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i]!)) seen++
    if (seen === digitsBefore) return i + 1
  }
  return formatted.length
}

/** Сумма денежных значений без float-хвостов — складываем копейки. */
export function sumMoney(values: number[]): number {
  return values.reduce((sum, v) => sum + toUnits(v, 'money'), 0) / 100
}
